import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  VisibilityState,
  Row,
} from '@tanstack/react-table';
import {
  Button, Collapse, ListItemText, Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FilterIcon from '../../assets/icons/filter-icon.svg';
import EditIcon from '../../assets/icons/edit-icon.svg';
import EmbedIcon from '../../assets/icons/embed-icon.svg';
import DeleteIcon from '../../assets/icons/delete-icon.svg';
import KeyIcon from '../../assets/icons/key-icon.svg';
import PlusIcon from '../../assets/icons/plus-icon.svg';
import CopyIcon from '../../assets/icons/copy-link_img.svg';
import PinIcon from '../../assets/icons/pinIcon.svg';
import UnpinIcon from '../../assets/icons/unPinIcon.svg';
import TableFilters from '../tableFilters/tableFilters';
import CustomTablePagination from '../customTablePagination/customTablePagination';
import { useToggledTableColumnsStore } from '../../store/store';
import {
  parseField,
  RenderEmptyTable,
  RenderError,
  RenderLoader,
} from './tableHelpers/tableHelpers';
import SwitchInput from '../inputs/switchInput';
import { Colors } from '../../constants/constants';
import CustomDateRangePicker from '../../partials/customDateRangePicker/customDateRangePicker';
import SecondaryButton from '../buttons/secondaryButton';
import { CustomButton } from '../buttons/customButton';
import TableExportButton from '../TableExportButton/tableExportButton';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import columnIcon from '../../assets/icons/columns_icon.png';
import SortingUpward from '../../assets/icons/sorting-upward.svg';
import SortingDown from '../../assets/icons/sorting-down.svg';
import Sorting from '../../assets/icons/sort.svg';
import check from '../../assets/icons/column_checkmark.svg';

interface BasicTableInterface {
    data: any,
    columns: TableColumnInterface[],
    isError: boolean,
    isFetching: boolean,
    isDeleting?: boolean,
    onEdit?: (_editObject: any) => void,
    onCopy?: (_copy: any) => void,
    onDelete?: (_id: number, item: any) => void,
    onEmbed?: (_id: string) => void,
    canPinRows?: (_id: number) => void,
    refetch: () => void,
    searchInput?: any,
    setSearchInput?: any,
    page: number,
    setPage: (_newPage: number) => void,
    setRowsPerPage: (_rowsPerPage: number) => void,
    rowsPerPage: number,
    setEditDialogIsOpen?: (_bool: boolean) => void,
    setIsEmbedDialogOpen?: (_bool: boolean) => void,
    formInputs?: any,
    setIsAreYouSureDialogOpen?: (_bool: boolean) => void,
    setCopyLinkDialogOpen?: (_bool: boolean) => void,
    canGenerateNewPassword?: boolean,
    canCopyLink?: boolean,
    handleNavigation?: (_campaignName: any) => void,
    customFilters?: any,
    extendedColumns?: TableColumnInterface[],
    sortBy?: string,
    onGeneratePassword?: (_item: any) => void;
    setSortBy?: (_sortBy: string) => void,
    orderBy?: string,
    setOrderBy?: (_orderBy: string) => void,
    sortablesList?: string[],
    filterDateRange?: DateRange<Dayjs>,
    setFilterDateRange?: (_newDateRange: DateRange<Dayjs>) => void,
    borderless?: boolean,
    hasNextPage?: boolean,
    fetchDownload?: (_acceptHeader: string) => void;
    fsTable?: boolean,
}

const BasicTable: React.FC<BasicTableInterface> = (props) => {
  const { t } = useTranslation();
    const [columns, setColumns] = useState(props.columns);
    const isTableColumnsToggled = useToggledTableColumnsStore((s) => s.isTableColumnsToggled);
  const setIsTableColumnsToggled = useToggledTableColumnsStore((s) => s.setIsTableColumnsToggled);
  const [searchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFilterListOpen, setIsFilterListOpen] = useState(
    Object.values(Object.fromEntries(searchParams.entries())).some((val) => val !== ''),
  );
  const [rowPinning, setRowPinning] = useState<{ top: string[] }>({ top: [] });
  const orderBy = props.orderBy === 'desc' ? '' : 'asc';

  const handleTogglePin = (itemId: number) => {
    const stringId = String(itemId);
    setRowPinning((prev) => {
      const isPinned = prev.top.includes(stringId);
      return {
        ...prev,
        top: isPinned
          ? prev.top.filter((id) => id !== stringId)
          : [...prev.top, stringId],
      };
    });
  };
  const sortedData = useMemo(() => {
    const rawData = props.fsTable ? props.data?.report ?? [] : props.data?.data ?? [];
    if (rowPinning.top.length > 0) {
      const pinnedRows = rawData.filter((item: any) => rowPinning.top.includes(String(item.id)));
      const unpinnedRows = rawData.filter((item: any) => !rowPinning.top.includes(String(item.id)));
      return [...pinnedRows, ...unpinnedRows];
    }

    return rawData;
  }, [props.data, rowPinning.top]);

    const customFieldKeys = useMemo(() => {
        const rows = props.fsTable
            ? props.data?.report ?? []
            : props.data?.data ?? [];

        const keys = new Set<string>();

        rows.forEach((row: any) => {
            if (row?.customField && typeof row.customField === 'object') {
                Object.keys(row.customField).forEach((key) => {
                    keys.add(key);
                });
            }
        });

        return Array.from(keys);
    }, [props.data, props.fsTable]);

    const customFieldColumns = useMemo(() => {
        return customFieldKeys.map((key) => ({
            field: `customField.${key}`,
            headerName: key,
            cellRenderer: (value: any) => value ?? 0,
        }));
    }, [customFieldKeys]);

  useEffect(() => {
    const stored = localStorage.getItem('pinnedRows');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRowPinning({ top: parsed });
      } catch {
      }
    }
  }, []);

  useEffect(() => {
    if (rowPinning?.top?.length) {
      localStorage.setItem('pinnedRows', JSON.stringify(rowPinning.top));
    } else {
      localStorage.removeItem('pinnedRows');
    }
  }, [rowPinning.top]);

  const initialVisibility = useMemo(() => {
    const stored = localStorage.getItem('columnVisibility');
    return stored ? JSON.parse(stored) : {};
  }, []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialVisibility);

  useEffect(() => {
    localStorage.setItem('columnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const toggleColumn = (field: string) => {
    setColumnVisibility((prev) => ({ ...prev, [field]: !(prev[field] !== false) }));
  };

  const isOpen = Boolean(anchorEl);

  const data = useMemo(() => (props.fsTable ? props.data?.report || [] : props.data?.data || []), [props.data]);
  const hasActions = !!props.onDelete || !!props.onEdit || props.canGenerateNewPassword
    || props.canCopyLink || props.onEmbed;

  const tableColumns = useMemo<ColumnDef<any, any>[]>(() => {
    const baseCols: ColumnDef<any, any>[] = columns.map((col) => ({
      accessorKey: typeof col.field === 'string' && col.field.endsWith('.name')
        ? col.field.replace(/\.name$/, '')
        : col.field,
      header: () => (
        <div
            onClick={() => {
                if (!props.sortablesList?.includes(col.field)) return;

                if (props.sortBy !== col.field) {
                    props.setSortBy?.(col.field);
                    props.setOrderBy?.('asc');
                } else if (props.orderBy === 'asc') {
                    props.setOrderBy?.('desc');
                } else if (props.orderBy === 'desc') {
                    props.setSortBy?.('');
                    props.setOrderBy?.('');
                }
            }}
          className="flex cursor-pointer items-center gap-1 text-left text-xs uppercase text-primary"
        >
          {t(col.headerName)}
          {props.sortBy === col.field ? (
            props.orderBy === 'asc' ? (
              <img alt="asc" src={SortingUpward} className="ml-1 inline-block h-[15px] w-[15px]" />
            ) : (
              <img alt="asc" src={SortingDown} className="ml-1 inline-block h-[15px] w-[15px]" />
            )
          ) : (
            <img alt="sorting" src={Sorting} className="ml-1 inline-block h-[15px] w-[15px]" />
          )}
        </div>
      ),

      cell: ({ row }: { row: Row<any> }) => {
        const value = parseField(col.field, row.original);
        return (
          <td
            className="min-w-[75px] max-w-[150px] truncate whitespace-nowrap border-r
            border-[#EAEAEA] px-6 py-4 text-sm text-primary"
            onClick={() => props.handleNavigation?.(row.original)}
          >
            {col.cellRenderer && value !== undefined ? (
              col.headerName === 'ctr' ? `${value > 0 ? value.toFixed(2) : 0}%` : col.cellRenderer(value)
            ) : (
              <Tooltip title={value}><span>{value}</span></Tooltip>
            )}
          </td>
        );
      },
    }));

    if (hasActions && columns === props.columns) {
      baseCols.push({
        accessorKey: 'actions',
        header: () => <p className="text-center text-xs uppercase text-primary">{t('actions')}</p>,
        cell: ({ row }: { row: Row<any> }) => {
          const item = row.original;
          return (
            <td className="max-w-[100px]">
              <div className="flex justify-around">
                {props.canCopyLink && (
                <Tooltip title={t('copyAdLink')}><Button onClick={(e) => { e.stopPropagation(); props.onCopy?.(item); props.setCopyLinkDialogOpen?.(true); }}  sx={{ minWidth: '40px' }}><img className="h-[20px] w-[20px]" src={CopyIcon} alt="" /></Button></Tooltip>
                )}
                {props.canGenerateNewPassword && (
                <Tooltip title={t('generateNewPassword')}>
                  <Button onClick={(e) => {
                    e.stopPropagation();
                    props.onGeneratePassword?.(item);
                  }}
                          sx={{ minWidth: '40px'}}
                  >
                    <img className="h-[20px] w-[20px]" src={KeyIcon} alt="" />
                  </Button>
                </Tooltip>
                )}
                {props.onEdit && (
                <Tooltip title={t('editRow')}>
                  <Button onClick={(e) => {
                    e.stopPropagation();
                    props.onEdit?.(item); props.setEditDialogIsOpen?.(true);
                  }}
                          sx={{ minWidth: '40px' }}
                  >
                    <img className="h-[20px] w-[20px]" src={EditIcon} alt="" />
                  </Button>
                </Tooltip>
                )}
                {props.onDelete && (
                <Tooltip title={t('deleteRow')}>
                  <Button onClick={(e) => {
                    e.stopPropagation(); props.onDelete?.(item.id, item);
                  }}
                          sx={{ minWidth: '40px' }}
                  >
                    <img className="h-[20px] w-[20px]" src={DeleteIcon} alt="" />
                  </Button>
                </Tooltip>
                )}
                {props.onEmbed && (
                <Tooltip title={t('embed')}>
                  <Button onClick={(e) => {
                    e.stopPropagation(); props.onEmbed?.(item.id);
                    props.setIsEmbedDialogOpen?.(true);
                  }}
                          sx={{ minWidth: '40px' }}
                  >
                    <img className="h-[20px] w-[20px]" src={EmbedIcon} alt="" />
                  </Button>
                </Tooltip>
                )}
                {props.canPinRows && (
                  <Tooltip title={rowPinning.top.includes(String(item.id)) ? t('unpinRow') : t('pinRow')}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePin(item.id);
                      }}
                      sx={{ minWidth: '40px' }}
                    >
                      <img
                        className="h-[20px] w-[20px]"
                        src={rowPinning.top.includes(String(item.id)) ? UnpinIcon : PinIcon}
                        alt="Pin"
                      />
                    </Button>
                  </Tooltip>
                )}
              </div>
            </td>
          );
        },
      });
    }
    return baseCols;
  }, [columns, props, rowPinning.top]);

  const table = useReactTable({
    data: sortedData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
  });

  return (
    <div className="w-full rounded-lg border border-[#EAEAEA] bg-white">
      <div className="flex items-center justify-between p-4">
        {props.extendedColumns ? (
            <button
                onClick={() => {
                    const isOpening = columns === props.columns;

                    if (isOpening) {
                        setColumns([
                            ...props.columns,
                            ...(props.extendedColumns ?? []),
                            ...customFieldColumns,
                        ]);
                    } else {
                        setColumns(props.columns);
                    }

                    setIsTableColumnsToggled(isOpening);
                }}
                className="flex items-center gap-2 rounded border px-4 py-2"
            >
          <p className="text-xs text-primary">{t('moreDetails')}</p>
          <SwitchInput checked={columns !== props.columns} />
        </button>
        ): <div/>}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 hover:cursor-pointer">
            <div
              className="flex h-[40px] cursor-pointer items-center justify-center gap-2 rounded-lg border bg-white
              px-4 py-1.5 text-primary hover:bg-[#9992] hover:opacity-95"
              role="button"
              tabIndex={0}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setAnchorEl(e.currentTarget);
                }
              }}
            >
              <img src={columnIcon} />
              Columns
            </div>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={() => setAnchorEl(null)}
            >
              {columns.map((col) => {
                const baseField = typeof col.field === 'string'
                  ? col.field.split('.')[0]
                  : col.field;

                return (
                  <MenuItem key={baseField} onClick={() => toggleColumn(baseField)}>
                    {columnVisibility[baseField] !== false && (
                      <img className="mr-4 h-[16px] w-[16px]" src={check} alt="tick" />
                    )}
                    <ListItemText>{t(col.headerName)}</ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
          {props.filterDateRange && props.setFilterDateRange && (
            <SecondaryButton
              onMouseDown={() => setIsCalendarOpen(true)}
              style={{
                paddingInline: '1rem',
                border: `1px solid ${isCalendarOpen ? Colors.primary : '#eaeaea'}`,
              }}
            >
              <CustomDateRangePicker
                filterRange={props.filterDateRange}
                setFilterRange={props.setFilterDateRange}
                isCalendarOpen={isCalendarOpen}
                setIsCalendarOpen={setIsCalendarOpen}
                iconButton
                showFilterDates
              />
            </SecondaryButton>
          )}
          <TableExportButton fetchDownload={props.fetchDownload} />
          <Tooltip title={t('toggleFilters')}>
            <CustomButton
              variant="iconOutlined"
              onClick={() => setIsFilterListOpen((prev) => !prev)}
            >
              <img src={FilterIcon} alt="filter" className="h-4 w-4" />
            </CustomButton>
          </Tooltip>
          {props.onEdit && (
            <Tooltip title={t('newRow')}>
              <CustomButton
                variant="icon"
                onClick={() => {
                  props.onEdit?.(props.formInputs);
                  props.setEditDialogIsOpen?.(true);
                }}
              >
                <img src={PlusIcon} alt="plus" className="h-4 w-4" />
              </CustomButton>
            </Tooltip>
          )}
        </div>
      </div>
      <Collapse in={props.searchInput && isFilterListOpen} timeout="auto" unmountOnExit className="px-4">
        <TableFilters
          searchInput={props.searchInput}
          setSearchInput={props.setSearchInput}
          refetch={props.refetch}
          setPage={props.setPage}
        >
          {props.customFilters}
        </TableFilters>
      </Collapse>
      <div className="overflow-x-auto border-y border-[#EAEAEA]">
        <table className={`w-full border-collapse ${columns !== props.columns ? 'table-auto' : 'table-fixed'}`}>
          <thead className="bg-[#F5F5F5]">
            {table.getHeaderGroups()
              .map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`whitespace-nowrap border-r border-[#EAEAEA] px-6 py-4 text-sm text-primary
                       ${columns !== props.columns ? '' : 'min-w-[75px] max-w-[150px] truncate'}`}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
          </thead>
          <tbody>
            {!props.isFetching && !props.isError && table.getRowModel()
              .rows
              .map((row) => (
                <tr
                  key={row.id}
                  className={rowPinning.top.includes(String(row.original.id)) ? 'border-t  border-[#EAEAEA] bg-[#FFECD2]'
                    : 'border-t border-[#EAEAEA] hover:bg-[#FAFAFA]'}
                >
                  {row.getVisibleCells()
                    .map((cell) => (
                      <React.Fragment key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </React.Fragment>
                    ))}
                </tr>
              ))}
            {props.isError && <RenderError columnLength={columns.length + (hasActions ? 1 : 0)} />}
            {!props.isFetching && !props.isError && data.length === 0
            && <RenderEmptyTable columnLength={columns.length + (hasActions ? 1 : 0)} />}
            {props.isFetching && <RenderLoader columnLength={columns.length + (hasActions ? 1 : 0)} />}
          </tbody>
        </table>
      </div>
      {!props.isFetching && (
        <CustomTablePagination
          count={props.data?.meta?.total}
          page={props.page}
          rowsPerPage={props.rowsPerPage}
          setPage={props.setPage}
          refetch={props.refetch}
          setRowsPerPage={props.setRowsPerPage}
          hasNextPage={props.hasNextPage}
        />
      )}
    </div>
  );
};

// @ts-ignore
export default BasicTable;
